<script lang="ts" setup>
import { inject, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { ManifestHandlerEvents } from '@/viewerExtensions/events';
import { isValidJSON } from '@/utils';
import CustomPropertyList from '@/components/CustomConfig/CustomPropertyList.vue';
import CreateCustomProperty from '@/components/CustomConfig/CreateCustomProperty.vue';

import type { Ref } from 'vue';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';
import type { ManifestHandler } from '@/viewerExtensions';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const customProperties = ref(new Map<string, unknown>());
const hookedManifestLoadedEvent = ref(false);
const manifestHandlerExtension = viewer.value?.extensions.get('ManifestHandler') as Extension<ManifestHandler>;

provide('customProperties', customProperties);

const generateCustomPropertiesTree = () => {
  const emptyManifestKeys = Object.keys(manifestHandlerExtension.emptyManifest);
  const currentManifestKeys = Object.keys(manifest.value);

  const newManifestKeys = currentManifestKeys.filter((key) => !emptyManifestKeys.includes(key));

  for (const key of newManifestKeys) {
    customProperties.value.set(key, manifest.value[key as keyof IManifest]);
  }
};

const manifestLoadedHandler = () => {
  generateCustomPropertiesTree();
};

const viewerWatcher = (newValue: Nullable<Viewer>) => {
  if (!newValue) return;

  if (hookedManifestLoadedEvent.value === false) {
    newValue.addEventListener(ManifestHandlerEvents.MANIFEST_LOADED, manifestLoadedHandler);
    hookedManifestLoadedEvent.value = true;
  }
};

const mountHandler = () => {
  if (!viewer.value) return;

  generateCustomPropertiesTree();

  if (hookedManifestLoadedEvent.value === false) {
    viewer.value.addEventListener(ManifestHandlerEvents.MANIFEST_LOADED, manifestLoadedHandler);
    hookedManifestLoadedEvent.value = true;
  }
};

const unmountHandler = () => {
  if (!viewer.value) return;

  if (hookedManifestLoadedEvent.value === true) {
    viewer.value.removeEventListener(ManifestHandlerEvents.MANIFEST_LOADED, manifestLoadedHandler);
  }
};

const customPropertiesWatcher = () => {
  const emptyManifestKeys = Object.keys(manifestHandlerExtension.emptyManifest);

  for (const [key, value] of customProperties.value) {
    if (emptyManifestKeys.includes(key)) return;

    let finalValue: unknown = value;

    if (value === 'true') finalValue = true;
    else if (value === 'false') finalValue = false;
    else if (value === 'null') finalValue = null;
    else if (typeof value === 'string' && isValidJSON(value)) finalValue = JSON.parse(value);

    manifest.value[key as keyof IManifest] = finalValue;

    if (manifestHandlerExtension.sceneJson) {
      manifestHandlerExtension.sceneJson[key as keyof IManifest] = finalValue;
    }
  }
};

watch(customProperties, customPropertiesWatcher, { deep: true });
watch(viewer, viewerWatcher);
onMounted(mountHandler);
onBeforeUnmount(unmountHandler);
</script>

<template>
  <CustomPropertyList />
  <CreateCustomProperty />
</template>
